import _, {isUndefined} from 'lodash';
import {createSelector} from 'reselect';

import {AppParametricSelector} from '../../../helpers/reselectHelpers';
import {CardModel} from '../../../models/api/cardModel';
import {AppState} from '../../appState';
import {getCards} from '../../data/dataSelectors';
import {getFlatPanels} from '../panelsSelectors';
import {ContactDetailsPanelId, PanelTypesEnum} from '../panelStateBase';
import {ContactDetailsPanelState} from './contactDetailsPanelState';

/*
 * Panel selectors.
 */

interface ContactDetailsPanelProps {
  panelId: ContactDetailsPanelId;
}

function getPanel(state: AppState, props: ContactDetailsPanelProps) {
  return state.panels[props.panelId];
}

export function makeGetContactDetailsPanel() {
  return createSelector(getPanel, (panel) => {
    if (!panel || panel.type !== PanelTypesEnum.CONTACT_DETAILS) {
      return null;
    }

    return panel;
  });
}

export const getFirstContactDetailPanel = createSelector([getFlatPanels], (panels) =>
  _(panels)
    .map((panel) => panel.type === PanelTypesEnum.CONTACT_DETAILS && panel.selectedContactUid && panel)
    .compact()
    .first(),
);

export function makeGetContactDetailsPanelSubState() {
  return createSelector(makeGetContactDetailsPanel(), (panel) => panel?.subState);
}

export function makeGetDuplicateCard<P>(
  getContactDetailsPanel: AppParametricSelector<P, ContactDetailsPanelState | null>,
  getSelectedCard: AppParametricSelector<P, CardModel | undefined>,
) {
  return createSelector(
    [getContactDetailsPanel, getCards, getSelectedCard],
    (contactDetailsPanel, cards, selectedCard) => {
      if (!contactDetailsPanel || contactDetailsPanel.cardIds.length <= 1 || !selectedCard) {
        return undefined;
      }

      // Filter out the current selected card
      const cardId = _(contactDetailsPanel.cardIds)
        .filter((cId) => cId !== selectedCard.id && !isUndefined(cards[cId]))
        .first();
      if (!cardId) {
        return undefined;
      }

      return cards[cardId];
    },
  );
}
